import { PageBreadcrumb } from '@repo/shared-client/components/blocks'
import {
  Button,
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@repo/shared-client/components/ui'
import { createFileRoute, Outlet, useParams } from '@tanstack/react-router'
import { DocsSidebar } from '@web/src/features/docs/docs-sidebar'
import { useTranslation } from '@web/src/lib/intl/react'
import { allDocs } from 'content-collections'
import type { TFunction } from 'i18next'
import { MenuIcon, XIcon } from 'lucide-react'
import { useEffect, useState } from 'react'

const MDX_EXT_REGEX = /\.(md|mdx)$/
const WORD_SEPARATOR_REGEX = /[-_]/

export const Route = createFileRoute('/(public)/(main-layout)/helpcenter')({
  component: DocsLayout,
})

function DocsLayout() {
  const params = useParams({ strict: false })
  const slug = params?._splat as string | undefined
  const [drawerOpen, setDrawerOpen] = useState(false)
  const { t } = useTranslation()

  useEffect(() => {
    setDrawerOpen(false)
  }, [slug])

  const breadcrumbs = buildBreadcrumbs(slug, t)

  return (
    <div className="mx-auto w-full max-w-7xl p-4">
      <div className="flex gap-8">
        <aside className="hidden w-[200px] shrink-0 md:block">
          <DocsSidebar />
        </aside>

        <main className="min-w-0 flex-1">
          <Drawer onOpenChange={setDrawerOpen} open={drawerOpen}>
            <div className="flex w-full flex-wrap items-center gap-2">
              <DrawerTrigger
                render={
                  <Button
                    aria-label={t('Open help center menu')}
                    className="flex md:hidden"
                    size="icon"
                    type="button"
                    variant="default"
                  />
                }
              >
                <MenuIcon className="size-5" />
              </DrawerTrigger>
              {breadcrumbs.length > 0 && (
                <PageBreadcrumb items={breadcrumbs.map((crumb) => ({ label: crumb.label, to: crumb.href }))} />
              )}
            </div>

            <DrawerContent className="w-[min(100vw-2rem,250px)] max-w-[min(100vw-2rem,250px)]" side="left">
              <DrawerHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
                <DrawerTitle>{t('Help Center')}</DrawerTitle>
                <DrawerClose className="inline-flex size-8 shrink-0 items-center justify-center rounded-md text-foreground outline-none hover:bg-muted">
                  <XIcon aria-hidden className="size-4" />
                  <span className="sr-only">{t('Close')}</span>
                </DrawerClose>
              </DrawerHeader>
              <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
                <DocsSidebar embedded />
              </div>
            </DrawerContent>
          </Drawer>

          <div className="pt-2">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  )
}

interface BreadcrumbItem {
  label: string
  slug?: string
  href: string
}

function buildBreadcrumbs(slug: string | undefined, t: TFunction): BreadcrumbItem[] {
  const crumbs: BreadcrumbItem[] = []

  crumbs.push({
    label: t('Help Center'),
    href: '/helpcenter/intro',
  })

  if (!slug) {
    return crumbs
  }

  const segments = slug.split('/').filter(Boolean)

  for (let i = 0; i < segments.length; i++) {
    const segmentPath = segments.slice(0, i + 1).join('/')
    const segment = segments[i]

    const doc = allDocs.find((d) => {
      const pathWithoutExt = d._meta.path.replace(MDX_EXT_REGEX, '')
      return pathWithoutExt === segmentPath || d._meta.path === segmentPath
    })

    const label = doc?.sidebar_label || doc?.title || formatSegmentName(segment)

    crumbs.push({
      label,
      slug: segmentPath,
      href: `/helpcenter/${segmentPath}`,
    })
  }

  return crumbs
}

function formatSegmentName(segment: string): string {
  return segment
    .split(WORD_SEPARATOR_REGEX)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}
